import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Form, Input, InputNumber } from "antd";
import moment from "moment";
import Plus from "../../assets/Plus";
import SkeletonTable from "../../components/table/SkeletonTable";
import upgradePlansColumns from "../../columns/upgradePlans/upgradePlans";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import LoadableButton from "../../components/button/LoadableButton";
import ModalComponent from "../../components/global/modals/ModalComponent";
import ConfirmationModal from "../../components/global/modals/ConfirmationModal";
import {
  AddUpgradePlanApi,
  ChangeUpgradePlanStatusApi,
  DeleteUpgradePlanApi,
  EditUpgradePlanApi,
  UpgradePlanListApi,
} from "../../api/request/upgradePlans";

const UpgradePlans = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const { data } = await UpgradePlanListApi();

      setData(
        data?.data?.map((item) => ({
          ...item,
          description: (
            <p className="w-[250px] truncate" title={item?.description}>
              {item?.description}
            </p>
          ),
          created_at: item?.created_at
            ? moment(item?.created_at).format("lll")
            : "--",
        })),
      );
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
        status: values?.status ? "active" : "inactive",
      };

      if (editingPlan)
        await EditUpgradePlanApi({ id: editingPlan?.id, ...payload });
      else await AddUpgradePlanApi(payload);

      fetchPlans();
      setIsModalOpen(false);
      form.resetFields();
      setEditingPlan(null);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmStatus = async () => {
    if (!selectedPlan) return;
    setIsConfirmLoading(true);
    try {
      await ChangeUpgradePlanStatusApi({
        id: selectedPlan.id,
        status: newStatus,
      });
      fetchPlans();
      setIsConfirmOpen(false);
      setSelectedPlan(null);
      setNewStatus(null);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPlan) return;
    setIsDeleteLoading(true);
    try {
      await DeleteUpgradePlanApi({ id: selectedPlan.id });
      fetchPlans();
      setIsDeleteOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingPlan(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      no_of_users: plan?.no_of_users,
      duration: plan?.duration,
      amount: plan?.amount,
      status: plan?.status,
      description: plan?.description,
    });
    setIsModalOpen(true);
  };

  return (
    <div>
      <div className="border bg-white rounded-xl py-3">
        <div className="flex items-center justify-between sm:px-4 max-sm:px-2 mb-1">
          <h2 className="text-xl font-semibold">Upgrade Plans</h2>
          <button
            className="flex items-center gap-2 m-2 px-4 py-2 bg-secondary text-white rounded-md max-sm:px-1 max-sm:py-1"
            onClick={openAddModal}
          >
            <Plus className="w-5 h-5" />
            <span className="max-sm:text-xs">Add Upgrade Plan</span>
          </button>
        </div>

        <SkeletonTable
          columns={upgradePlansColumns({
            setSelectedPlan,
            setNewStatus,
            setIsConfirmOpen,
            GlobalTooltip,
            openEditModal,
            isDeleteLoading,
            setIsDeleteOpen,
          })}
          data={data}
          isLoading={isLoading}
          pagination={true}
        />
      </div>

      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={editingPlan ? "Edit Upgrade Plan" : "Add Upgrade Plan"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: true }}
        >
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item
              label="No of Users"
              name="no_of_users"
              rules={[{ required: true, message: "Please enter no of users" }]}
            >
              <InputNumber
                min={1}
                className="w-full"
                placeholder="Enter no of users"
              />
            </Form.Item>

            <Form.Item
              label="Duration (in months)"
              name="duration"
              rules={[{ required: true, message: "Please enter duration" }]}
            >
              <InputNumber
                min={1}
                className="w-full"
                placeholder="Enter duration"
              />
            </Form.Item>

            <div className="col-span-2">
              <Form.Item
                label="Amount"
                name="amount"
                rules={[{ required: true, message: "Please enter amount" }]}
              >
                <InputNumber
                  min={0}
                  className="w-full"
                  placeholder="Enter amount"
                />
              </Form.Item>
            </div>

            <div className="col-span-2">
              <Form.Item
                label="Description"
                name="description"
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <Input.TextArea
                  className="w-full"
                  placeholder="Enter description"
                  autoSize={{ minRows: 3, maxRows: 5 }}
                />
              </Form.Item>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-4">
            <LoadableButton
              className="btn-primary"
              type="submit"
              lable={editingPlan ? "Update" : "Add"}
              loadingLable="Saving..."
              isLoading={isLoading}
            />
          </div>
        </Form>
      </ModalComponent>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        setIsOpen={setIsConfirmOpen}
        type="warning"
        message={`Are you sure you want to set this plan as ${newStatus}?`}
        onConfirm={handleConfirmStatus}
        isLoading={isConfirmLoading}
        loadingLabel="Updating..."
      />

      <ConfirmationModal
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        type="error"
        message={`Are you sure you want to delete "${selectedPlan?.name}"?`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
};

export default UpgradePlans;
